import axios from "axios";
import { buildServiceNowUrl, getServiceNowHeaders } from "../services/serviceNow";

/**
 * Microsoft Graph Utilities
 * 
 * Gets Graph tokens from ServiceNow (not MSAL) through the app's same-origin
 * /api routes. Local dev uses the CRA proxy; deployed builds use the SWA API.
 */

export const MS_GRAPH_BASE = "https://graph.microsoft.com/v1.0";
const TOKEN_PATHS = [
  {
    path: "/api/x_verge_gather/smart_folders/sharepoint_token",
    method: "get",
    includeBasicAuth: true,
    label: "sharepoint_token",
  },
  {
    path: "/api/x_verge_gather/smart_folders/gettoken",
    method: "get",
    includeBasicAuth: false,
    label: "gettoken",
  },
  {
    path: "/api/x_verge_gather/smart_folders/gettoken_v2",
    method: "post",
    includeBasicAuth: false,
    label: "gettoken_v2",
  },
];

function extractTokenFromPayload(data) {
  return (
    data?.result?.token ||
    data?.result?.access_token ||
    data?.access_token ||
    data?.token ||
    null
  );
}

function isMissingResourceError(error) {
  const status = error?.response?.status;
  const message =
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    "";

  return status === 400 && /does not represent any resource/i.test(String(message));
}

/**
 * Get a Graph token from the ServiceNow smart_folders endpoints.
 */
export async function getGraphToken() {
  console.log("[Graph] getGraphToken: requesting token from ServiceNow");

  const requestSharePointToken = async (requestHeaders) => {
    let lastError = null;

    for (const tokenRoute of TOKEN_PATHS) {
      try {
        const url = buildServiceNowUrl(tokenRoute.path);
        const headers = getServiceNowHeaders({
          includeBasicAuth: tokenRoute.includeBasicAuth,
          extraHeaders: requestHeaders,
        });

        const resp =
          tokenRoute.method === "post"
            ? await axios.post(url, {}, { headers })
            : await axios.get(url, { headers });

        const token = extractTokenFromPayload(resp?.data);
        if (token) {
          console.log(`[Graph] getGraphToken: resolved via ${tokenRoute.label}`);
          return token;
        }

        console.error("[Graph] No token in response:", resp?.data);
        throw new Error(`Unable to obtain Graph token from ServiceNow via ${tokenRoute.label}`);
      } catch (error) {
        lastError = error;
        if (isMissingResourceError(error)) {
          console.warn(`[Graph] ${tokenRoute.label} route not found, trying fallback`);
          continue;
        }
        throw error;
      }
    }

    throw lastError || new Error("Unable to obtain Graph token from ServiceNow");
  };

  try {
    const token = await requestSharePointToken({});
    console.log("[Graph] getGraphToken: success, token length:", token.length);
    return token;
  } catch (error) {
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
      throw new Error(
        "ServiceNow authentication failed while requesting the SharePoint token. Check the demo credentials or required user token."
      );
    }
    console.error("[Graph] getGraphToken: error", error);
    throw error;
  }
}

/**
 * Fetch a SharePoint item blob using the shares API (from URL)
 */
export async function fetchSharePointItemBlobFromUrl(webUrl) {
  console.log("[Graph] fetchSharePointItemBlobFromUrl:", webUrl);
  
  const token = await getGraphToken();
  
  // Use the shares API to get the driveItem from URL
  const base64 = btoa(webUrl);
  const base64url = base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
  const shareUrl = `${MS_GRAPH_BASE}/shares/u!${base64url}/driveItem`;
  
  // First, get the driveItem to find driveId and itemId
  const itemResp = await fetch(shareUrl, {
    headers: { Authorization: `Bearer ${token}` }
  });
  
  if (!itemResp.ok) {
    const text = await itemResp.text().catch(() => "");
    console.error("[Graph] shares lookup failed:", itemResp.status, text);
    throw new Error(`Graph shares lookup failed: ${itemResp.status} ${itemResp.statusText}`);
  }
  
  const item = await itemResp.json();
  const driveId = item?.parentReference?.driveId;
  const itemId = item?.id;
  
  if (!driveId || !itemId) {
    throw new Error("Could not resolve driveId/itemId from URL");
  }
  
  // Now fetch the content
  const contentUrl = `${MS_GRAPH_BASE}/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}/content`;
  console.log("[Graph] Fetching content from:", contentUrl);
  
  const blobResp = await fetch(contentUrl, {
    headers: { Authorization: `Bearer ${token}` }
  });
  
  if (!blobResp.ok) {
    const text = await blobResp.text().catch(() => "");
    console.error("[Graph] content fetch failed:", blobResp.status, text);
    throw new Error(`Graph file fetch failed: ${blobResp.status} ${blobResp.statusText}`);
  }
  
  const blob = await blobResp.blob();
  console.log("[Graph] fetchSharePointItemBlobFromUrl: success, size:", blob.size);
  return blob;
}

/**
 * Fetch blob by SharePoint IDs (siteId, listId, listItemId)
 */
export async function fetchBlobBySharePointIds(sharepointIds) {
  console.log("[Graph] fetchBlobBySharePointIds:", sharepointIds);
  
  if (!sharepointIds) throw new Error("sharepointIds required");
  const { siteId, listId, listItemId } = sharepointIds;
  
  const token = await getGraphToken();
  
  // Get the driveItem from the list item
  const driveItemUrl = `${MS_GRAPH_BASE}/sites/${encodeURIComponent(siteId)}/lists/${encodeURIComponent(listId)}/items/${encodeURIComponent(listItemId)}/driveItem`;
  
  const resp = await fetch(driveItemUrl, {
    headers: { Authorization: `Bearer ${token}` }
  });
  
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`listItemId path failed: ${resp.status} ${resp.statusText} ${text}`);
  }
  
  const driveItem = await resp.json();
  
  // Download the content
  const downloadUrl = `${MS_GRAPH_BASE}/drives/${encodeURIComponent(driveItem.parentReference?.driveId)}/items/${encodeURIComponent(driveItem.id)}/content`;
  
  const blobResp = await fetch(downloadUrl, {
    headers: { Authorization: `Bearer ${token}` }
  });
  
  if (!blobResp.ok) {
    const text = await blobResp.text().catch(() => "");
    throw new Error(`download failed: ${blobResp.status} ${blobResp.statusText} ${text}`);
  }
  
  return await blobResp.blob();
}

/**
 * Fetch blob by driveId and itemId
 */
export async function fetchSharePointItemBlobByDriveRef(driveId, itemId) {
  console.log("[Graph] fetchSharePointItemBlobByDriveRef:", { driveId, itemId });
  
  const token = await getGraphToken();
  const url = `${MS_GRAPH_BASE}/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}/content`;
  
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` }
  });
  
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Graph file fetch failed: ${response.status} ${response.statusText}`);
  }
  
  return await response.blob();
}

/**
 * Fetch blob by itemId (requires MS_GRAPH_DRIVE_ID config or default drive)
 */
export async function fetchSharePointItemBlob(itemId) {
  console.log("[Graph] fetchSharePointItemBlob:", itemId);
  // This would need a configured drive ID - use fetchSharePointItemBlobByDriveRef instead
  throw new Error("fetchSharePointItemBlob requires a drive ID - use fetchSharePointItemBlobByDriveRef or fetchSharePointItemBlobFromUrl");
}

/**
 * Resolve Graph identity from a file URL
 */
export async function resolveGraphItemIdFromSmartFile(file) {
  if (!file) return null;
  const webUrl = file.url || file.webUrl;
  if (!webUrl) return null;
  
  // Skip non-SharePoint/OneDrive URLs
  try {
    const hostname = new URL(webUrl).hostname.toLowerCase();
    const isMsHost = hostname.includes("sharepoint") || hostname.includes("onedrive");
    if (!isMsHost) {
      return { graphItemId: null, sharepointIds: null };
    }
  } catch (_) {
    return { graphItemId: null, sharepointIds: null };
  }
  
  try {
    const token = await getGraphToken();
    const base64 = btoa(webUrl);
    const base64url = base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
    const url = `${MS_GRAPH_BASE}/shares/u!${base64url}/driveItem?$select=id,name,webUrl,sharepointIds`;
    
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    if (!response.ok) {
      return { graphItemId: null, sharepointIds: null };
    }
    
    const item = await response.json();
    return {
      graphItemId: item?.id,
      sharepointIds: item?.sharepointIds || null,
    };
  } catch (e) {
    console.warn("[Graph] Could not resolve Graph id for file", e);
    return { graphItemId: null, sharepointIds: null };
  }
}
